/**
 * Featurebase integration: Feedback Widget + Messenger Widget.
 *
 * Loads the Featurebase SDK once and initializes both widgets.
 * Must be rendered inside ThemeProvider so useTheme() is available.
 *
 * File organization:
 *   1. SHARED      — SDK loading, theme resolution, types
 *   2. FEEDBACK    — Bug reports / feature requests widget
 *   3. MESSENGER   — Chat / help center widget
 *   4. PROVIDER    — Orchestrates both widgets
 */

import { useAuth } from '@clerk/react'
import { useEffect, useRef } from 'react'
import { useChrome } from '@/hooks/use-chrome'
import { trpc } from '@/lib/trpc'
import { useTheme } from './theme-provider'

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED — SDK loading, theme resolution, types
// ═══════════════════════════════════════════════════════════════════════════════

const FEATUREBASE_SDK_ID = 'featurebase-sdk'
const FEATUREBASE_SDK_URL = 'https://do.featurebase.app/js/sdk.js'

// The SDK caches identity in localStorage and reuses it across reloads. A stale
// entry keeps a logged-out browser pinned to the authenticated "Users" module
// config (which fetches the secured changelog and 401s) instead of falling back
// to the "Visitors" config. Clearing both keys forces a clean anonymous session.
const FEATUREBASE_IDENTITY_KEYS = [
  'featurebaseGlobalAuth',
  'featurebaseIdentifyData',
]

const FEATUREBASE_ORG = 'beyondctci'

/** Drop persisted Featurebase identity so a logged-out session stays anonymous. */
function clearFeaturebaseIdentity(): void {
  if (typeof window === 'undefined') return
  try {
    for (const key of FEATUREBASE_IDENTITY_KEYS) {
      window.localStorage.removeItem(key)
    }
    // Overwrite with org-only auth so the SDK does not reload a stale JWT from
    // localStorage on the next read. The in-memory global auth object can still
    // hold a JWT until shutdown; avoid calling initialize_feedback_widget while
    // logged out because it can re-attach a cached JWT from in-memory state.
    window.localStorage.setItem(
      'featurebaseGlobalAuth',
      JSON.stringify({ organization: FEATUREBASE_ORG }),
    )
    window.dispatchEvent(new CustomEvent('identifyAuthChange', { detail: {} }))
  } catch {
    // ignore quota / privacy mode / storage disabled errors
  }
}

type FeaturebaseStub = ((...args: unknown[]) => void) & { q?: unknown[][] }

declare global {
  interface Window {
    Featurebase?: FeaturebaseStub
  }
}

function ensureFeaturebaseQueue(): void {
  if (typeof window === 'undefined') return
  if (typeof window.Featurebase !== 'function') {
    window.Featurebase = (...args: unknown[]) => {
      ;(window.Featurebase!.q = window.Featurebase!.q ?? []).push(args)
    }
  }
}

function injectFeaturebaseScript(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(FEATUREBASE_SDK_ID)) return

  const script = document.createElement('script')
  script.id = FEATUREBASE_SDK_ID
  script.src = FEATUREBASE_SDK_URL
  script.async = true
  document.head.appendChild(script)
}

// ═══════════════════════════════════════════════════════════════════════════════
// FEEDBACK WIDGET — bug reports / feature requests
// ═══════════════════════════════════════════════════════════════════════════════

/** Parse browser name from userAgent (order matters for Chrome/Edge). */
function getBrowserFromUA(ua: string): string {
  if (ua.includes('Edg/')) return 'Edge'
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera'
  if (ua.includes('Firefox/')) return 'Firefox'
  if (ua.includes('Chrome/') && !ua.includes('Chromium')) return 'Chrome'
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari'
  return 'Other'
}

/** Classify device as mobile, tablet, or desktop from userAgent. */
function getDeviceType(ua: string): 'mobile' | 'tablet' | 'desktop' {
  const u = ua.toLowerCase()
  if (u.includes('ipad') || (u.includes('tablet') && !u.includes('mobile')))
    return 'tablet'
  // Android without "Mobile" is often a tablet
  if (u.includes('android') && !u.includes('mobile')) return 'tablet'
  if (
    u.includes('mobile') ||
    u.includes('android') ||
    u.includes('iphone') ||
    u.includes('ipod') ||
    u.includes('webos') ||
    u.includes('blackberry')
  )
    return 'mobile'
  return 'desktop'
}

/** Parse OS/platform from userAgent. */
function getPlatformFromUA(ua: string): string {
  const u = ua.toLowerCase()
  if (u.includes('win')) return 'Windows'
  if (u.includes('mac')) return 'Mac'
  if (u.includes('linux') && !u.includes('android')) return 'Linux'
  if (u.includes('android')) return 'Android'
  if (u.includes('iphone') || u.includes('ipod')) return 'iOS'
  if (u.includes('ipad')) return 'iPadOS'
  return 'Other'
}

/**
 * Metadata to attach to bug reports (URL, browser, device, platform).
 * Safe to call in browser only; returns empty object when window is undefined.
 */
export function getBugReportMetadata(): Record<string, string> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined')
    return {}
  const ua = navigator.userAgent
  return {
    url: window.location.href,
    browser: getBrowserFromUA(ua),
    deviceType: getDeviceType(ua),
    platform: getPlatformFromUA(ua),
  }
}

/**
 * Opens the Featurebase feedback widget (e.g. from "Report bug").
 * Uses postMessage for the iframe-based feedback widget.
 * Attaches URL, browser, device type, and platform to the report.
 * No auth gate; call from UI to show the widget.
 */
export function openFeaturebaseWidget(): void {
  if (typeof window === 'undefined') return

  window.postMessage(
    {
      target: 'FeaturebaseWidget',
      data: { action: 'openFeedbackWidget' },
    },
    '*',
  )
  window.postMessage(
    {
      target: 'FeaturebaseWidget',
      data: {
        action: 'updateMetadata',
        metadata: getBugReportMetadata(),
      },
    },
    '*',
  )
}

/** Hook: initialize the feedback widget with org, theme, and user identity. */
function useInitFeedbackWidget(
  theme: 'dark' | 'light',
  authReady: boolean,
  isSignedIn: boolean | undefined,
  tokenData: { organization: string; featurebaseJwt: string } | undefined,
): void {
  useEffect(() => {
    if (typeof window.Featurebase !== 'function') return
    if (!authReady) return

    // Logged-out sessions must not mount the feedback widget: the SDK reuses its
    // in-memory JWT cache inside initialize_feedback_widget and forces the
    // authenticated "Users" messenger profile even when boot omits featurebaseJwt.
    if (isSignedIn === false) {
      window.Featurebase('destroy_feedback_widget')
      clearFeaturebaseIdentity()
      return
    }
    // Signed-in sessions should wait for JWT instead of clearing identity while
    // the token query is still loading.
    if (!tokenData) return

    window.Featurebase('identify', {
      organization: tokenData.organization,
      featurebaseJwt: tokenData.featurebaseJwt,
    })

    window.Featurebase('initialize_feedback_widget', {
      organization: FEATUREBASE_ORG,
      theme,
    })
  }, [theme, authReady, isSignedIn, tokenData])
}

// ═══════════════════════════════════════════════════════════════════════════════
// MESSENGER WIDGET — chat / help center
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Hook: boot the Featurebase Messenger widget.
 * Handles identity resolution, visibility gating, route-based hiding, and cleanup.
 * The messenger is hidden on the editor page to avoid obscuring the coding UI.
 */
function useInitMessenger(
  theme: 'dark' | 'light',
  authReady: boolean,
  isSignedIn: boolean | undefined,
  tokenData: { organization: string; featurebaseJwt: string } | undefined,
  isHidden: boolean,
): void {
  const bootedRef = useRef(false)
  const bootedWithJwtRef = useRef(false)
  const lastOnShowHandledAtRef = useRef(0)

  // Shutdown only on unmount — avoids premature frame wrapper destruction
  // when auth state or other dependencies change mid-lifecycle.
  useEffect(() => {
    return () => {
      if (bootedRef.current) {
        window.Featurebase?.('shutdown')
        bootedRef.current = false
        bootedWithJwtRef.current = false
      }
    }
  }, [])

  // Boot or shutdown the messenger based on visibility and auth state.
  // Shutdown is handled explicitly (isHidden toggle + unmount effect above),
  // NOT via effect cleanup, to prevent the SDK from destroying its frame
  // wrapper while async operations still reference it.
  useEffect(() => {
    if (typeof window.Featurebase !== 'function') return
    if (!authReady) return

    // Shutdown messenger when hidden
    if (isHidden) {
      if (bootedRef.current) {
        window.Featurebase('shutdown')
        bootedRef.current = false
        bootedWithJwtRef.current = false
      }
      return
    }

    // React Query may keep tokenData in cache after logout when the query is
    // disabled; isSignedIn is the source of truth for JWT usage.
    const hasAuthenticatedSession = isSignedIn === true && !!tokenData

    // Gate: signed-in users must wait for JWT before booting
    if (isSignedIn && !tokenData) return

    // Re-boot if messenger was booted anonymously but JWT is now available
    if (
      bootedRef.current &&
      hasAuthenticatedSession &&
      !bootedWithJwtRef.current
    ) {
      window.Featurebase('shutdown')
      bootedRef.current = false
    }

    // Re-boot without JWT after logout so stale identity is not reused
    if (bootedRef.current && isSignedIn === false && bootedWithJwtRef.current) {
      window.Featurebase('shutdown')
      clearFeaturebaseIdentity()
      bootedRef.current = false
      bootedWithJwtRef.current = false
    }

    // Don't boot if already booted
    if (bootedRef.current) return

    const appId = import.meta.env.VITE_FEATUREBASE_APPID_MESSENGER_SECRET
    if (!appId) return

    const bootParams: Record<string, unknown> = {
      appId,
      theme,
    }
    if (hasAuthenticatedSession) {
      bootParams.featurebaseJwt = tokenData.featurebaseJwt
    } else {
      // App-only connection: boot with appId, no user identity.
      clearFeaturebaseIdentity()
    }

    // IMPORTANT: This lifecycle is intentionally conservative because Featurebase
    // initialization order is brittle in real sessions. We have observed
    // regressions where anonymous users fall back to changelog (401s) when this
    // sequencing is "simplified". Do not refactor this block without validating:
    // 1) logged-out launcher opens Messages, 2) signed-in launcher opens changelog,
    // 3) logout does not reuse prior JWT identity.
    const onShowHandler = () => {
      // If fallback retries register the same handler multiple times, guard
      // against duplicate actions when the messenger opens.
      const now = Date.now()
      if (now - lastOnShowHandledAtRef.current < 200) return
      lastOnShowHandledAtRef.current = now

      if (hasAuthenticatedSession) {
        setTimeout(() => window.Featurebase?.('showChangelog'), 50)
        return
      }
      window.Featurebase?.('show', 'messages')
    }

    // Keep registration wrapped in try/catch: some SDK states throw "not
    // initialized" if onShow is attached too early.
    const registerOnShow = () => {
      try {
        window.Featurebase?.('onShow', onShowHandler)
      } catch {
        // Some SDK states throw until messenger initialization completes.
      }
    }

    let bootCallbackObserved = false
    const retryTimerIds: number[] = []
    window.Featurebase('boot', bootParams, () => {
      bootCallbackObserved = true
      registerOnShow()
    })
    // Keep callback-first registration plus timed fallback retries.
    // DO NOT replace this with a single path without manual runtime verification:
    // some SDK variants ignore the boot callback, while others invoke callback
    // before onShow registration is actually safe.
    //
    // Note: retries intentionally stop once callback is observed to preserve the
    // known-good behavior currently exercised by manual validation in this repo.
    for (const delay of [100, 250, 500, 1000, 2000]) {
      retryTimerIds.push(
        window.setTimeout(() => {
          if (bootCallbackObserved) return
          registerOnShow()
        }, delay),
      )
    }
    bootedRef.current = true
    bootedWithJwtRef.current = hasAuthenticatedSession

    // Preserve explicit shutdown lifecycle above while cleaning fallback timers.
    return () => {
      for (const timerId of retryTimerIds) {
        window.clearTimeout(timerId)
      }
    }
  }, [authReady, isSignedIn, theme, tokenData, isHidden])

  // Live theme sync — update without re-booting
  const prevThemeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!bootedRef.current) return
    if (prevThemeRef.current !== null && prevThemeRef.current !== theme) {
      window.Featurebase?.('setTheme', theme)
    }
    prevThemeRef.current = theme
  }, [theme])
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROVIDER — orchestrates both widgets
// ═══════════════════════════════════════════════════════════════════════════════

export function FeaturebaseProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { resolvedTheme } = useTheme()
  const { isSignedIn } = useAuth()
  const { data: tokenData } = trpc.user.getFeaturebaseToken.useQuery(
    undefined,
    { enabled: isSignedIn === true },
  )
  const authReady = isSignedIn !== undefined

  // Which pages hide the messenger is declared with the route and read back
  // through `useChrome`, rather than re-derived here from path prefixes.
  const { messenger } = useChrome()

  // Load SDK once on mount
  useEffect(() => {
    ensureFeaturebaseQueue()
    injectFeaturebaseScript()
  }, [])

  // Initialize feedback widget
  useInitFeedbackWidget(resolvedTheme, authReady, isSignedIn, tokenData)

  // Initialize messenger widget (hidden on editor, auth, and interview-room pages)
  useInitMessenger(resolvedTheme, authReady, isSignedIn, tokenData, !messenger)

  return <>{children}</>
}
